import Parser from 'html-react-parser';
import _ from 'lodash';
import moment from 'moment';

import {
  getState,
  isArchive,
  isTrash,
  isShare,
} from './App';
import { getSetting, getUserSetting } from './Settings';
import { getCurrentURLParams } from './URLParams';
import { userCan, userHasCap } from './Auth';

export const thumbnailPlaceholder = '/images/thumbnails/file.png';
export const thumbnailVideoPlaceholder = '/images/thumbnails/video.png';
export const thumbnailProcessing = '/images/thumbnails/processing.png';
export const thumbnailLink = '/images/thumbnails/link.png';

export const isImageFile = (fileType) => {
  const imageFileTypes = [
    'image/bmp',
    'image/gif',
    'image/jpg',
    'image/jpeg',
    'image/png',
    'image/tiff',
    'image/heif',
    'image/heic',
    'image/avif',
  ];

  return imageFileTypes.includes(fileType);
};

export const isVideoFile = (fileType) => {
  const videoFileTypes = [
    'video/mp4',
    'video/avi',
    'video/mov',
    'video/quicktime',
  ];

  return videoFileTypes.includes(fileType);
};

export const getTargetFileFormats = () => ([
  'jpg',
  'png',
  'webp',
]);

export const getFilePageUrl = ({
  fileUuid,
  shareUuid,
  shareCollectionUuid,
  backUrl,
}) => {
  let sharePath = `/shares/${shareUuid}`;
  if (shareCollectionUuid) {
    sharePath = `${sharePath}/collections/${shareCollectionUuid}`;
  }

  const getPathname = () => {
    if (shareUuid) {
      return `${sharePath}/files/${fileUuid}`;
    }
    if (isArchive()) {
      return `/archive/files/${fileUuid}`;
    }
    if (isTrash()) {
      return `/trash/files/${fileUuid}`;
    }
    return `/files/${fileUuid}`;
  };

  return {
    pathname: getPathname(),
    state: {
      modal: true,
      urlParamsOnClose: getCurrentURLParams(window.location),
      ...(backUrl ? { backUrl } : {}),
    },
  };
};

export const getFileExtension = (file) => {
  if (file.type === 'link' || file.type_group === 'link') {
    if (file.website) {
      return file.website.name;
    }
    return '';
  }

  let extension = typeof file.extension !== 'undefined' ?
    file.extension.toUpperCase() :
    (file.path && file.path.split('.').pop().toUpperCase());
  if (extension === 'JPEG') {
    extension = 'JPG';
  }

  return extension;
};

export const getFileNameFromPath = path => (
  path ? path.split('\\').pop().split('/').pop() : ''
);

export const getFileNameWithoutExtension = (filename = '', extension = '') => {
  const filenameValue = filename;
  const extensionValue = extension.toLowerCase();

  const ext = extensionValue.startsWith('.') ? extensionValue : `.${extensionValue}`;

  if (filenameValue.toLowerCase().endsWith(ext)) {
    return filenameValue.slice(0, -ext.length);
  } if (ext === '.jpg' && filenameValue.toLowerCase().endsWith('.jpeg')) {
    return filenameValue.slice(0, -5);
  }

  return filenameValue;
};

export const getFolderFromPath = path => (
  path ? path.substring(0, Math.max(path.lastIndexOf('/'), path.lastIndexOf('\\'))) : ''
);

const rawImageFileExtensions = [
  'arw',
  'cr2',
  'cr3',
  'crw',
  'dcr',
  'mrw',
  'nef',
  'orf',
  'raf',
  'dng',
];
export const isRawImageFile = (fileName) => {
  const extension = getFileExtension({ path: fileName }).toLowerCase();

  return rawImageFileExtensions.includes(extension);
};

export const isAnnotationableImage = (file) => {
  if (getFileExtension(file) === 'GIF') {
    return false;
  }

  const isImage = file.type_group === 'image' || isImageFile(file.path) || isRawImageFile(file.path);
  if (isImage) {
    if (file.urls.thumbnail.indexOf('/images/thumbnails/') < 0) {
      return true;
    }
  }
  return false;
};

const convertableFileFormats = [
  'pdf',
  'ai',
  'eps',
  'jpg',
  'jpeg',
  'png',
  'tif',
  'tiff',
  'avif',
  'ai',
  'eps',
  'svg',
  'heif',
  'heic',
  'psd',
];
const getConvertableFileFormats = () => (
  convertableFileFormats.concat(rawImageFileExtensions)
);

export const isConvertableImage = (file) => (
  getConvertableFileFormats().includes(getFileExtension(file).toLowerCase())
);

export const fileSupportsAnnotations = (file) => {
  const fileExtension = getFileExtension(file);

  if (['PDF'].includes(fileExtension)) {
    return true;
  }
  if (file.type_group === 'video') {
    return true;
  }
  if (isAnnotationableImage(file)) {
    return true;
  }

  return false;
};

const faceRecognitionFileFormats = [
  'jpg',
  'jpeg',
  'heif',
  'heic',
  'png',
  'tif',
  'tiff',
  'webp',
  'avif',
];
export const fileSupportsFaceRecognition = (file) => (
  faceRecognitionFileFormats
    .concat(rawImageFileExtensions)
    .includes(getFileExtension(file).toLowerCase())
);

export const getImageResolution = (file) => {
  let resolution = '';
  if (file && file.attributes) {
    if (file.attributes.width && file.attributes.height) {
      resolution = `${file.attributes.width} x ${file.attributes.height}`;
    }
  }

  return resolution;
};

export const getFileAccessIcon = (file) => {
  if ((file.parent || file.parent_uuid) && file.searchable === false) {
    return 'fa-eye-low-vision';
  }

  if (file.access_icon) {
    return `fa-${file.access_icon}`;
  }

  if (file.public) {
    return 'fa-eye';
  }

  return 'fa-lock';
};

export const getFileAccessText = (file, intl) => {
  if ((file.parent || file.parent_uuid) && file.searchable === false) {
    return intl.formatMessage({ defaultMessage: 'Show in workspace' });
  }

  if (file.access_description) {
    return file.access_description;
  }

  if (file.public) {
    return intl.formatMessage({ defaultMessage: 'Open' });
  }

  return intl.formatMessage({ defaultMessage: 'Restricted' });
};

export const isUploadComplete = (statusToCheck, statusEnum) => (
  statusToCheck === statusEnum.UPLOAD_FAILED
  || statusToCheck === statusEnum.UPLOAD_SUCCESSFUL
  || statusToCheck === statusEnum.CANCELED
);

export const formatBytes = (bytes, decimals = 2, unitInSpan = false) => {
  if (bytes === 0) {
    return '0 B';
  }

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  if (unitInSpan) {
    return Parser(`${parseFloat((bytes / (k ** i)).toFixed(parseInt(decimals)))} <span>${sizes[i]}</span>`);
  }

  return `${parseFloat((bytes / (k ** i)).toFixed(parseInt(decimals)))} ${sizes[i]}`;
};

export const emptyFilters = (filters) => {
  let empty = true;
  Object.keys(filters).forEach((filter) => {
    if (Array.isArray(filters[filter]) && filters[filter].length > 0) {
      empty = false;
    }
    if (!Array.isArray(filters[filter]) && filters[filter] !== null) {
      empty = false;
    }
  });

  return empty;
};

export const filtersCount = (filters) => {
  const ignoredFilters = [
    'date_from',
    'date_to',
    'original_date_from',
    'original_date_to',
    'expiration_date_from',
    'expiration_date_to',
  ];

  let count = 0;
  Object.keys(filters).forEach((filter) => {
    if (Array.isArray(filters[filter]) && filters[filter].length > 0) {
      count += filters[filter].length;
    }
    if (!Array.isArray(filters[filter]) && filters[filter] !== null) {
      if (!ignoredFilters.includes(filter)) {
        count += 1;
      }
    }
  });

  return count;
};

export const isPeopleFilterActive = () => {
  const state = getState();
  if (state) {
    return state.Files.selectedFilters.filter(sf => (sf.type === 'people')).length > 0;
  }

  return false;
};

export const hasResizableImages = files => (
  files.filter(f => (['JPG', 'JFIF', 'PNG', 'TIFF', 'BMP'].includes(getFileExtension(f)))).length > 0
);

export const hasConvertableImages = files => (
  files.filter(f => (
    getConvertableFileFormats().includes(getFileExtension(f).toLowerCase())
  )).length > 0
);

export const getFileSizeDescription = (fileSize, noBrackets = false) => {
  const descriptionFragments = [];

  if (fileSize.width && parseInt(fileSize.width) > 0) {
    descriptionFragments.push(`max. width: ${fileSize.width} px`);
  }
  if (fileSize.height && parseInt(fileSize.height) > 0) {
    descriptionFragments.push(`max. height: ${fileSize.height} px`);
  }
  if (fileSize.crop) {
    descriptionFragments.push('crop');
  }

  if (descriptionFragments.length > 0) {
    if (noBrackets) {
      return descriptionFragments.join(', ');
    }
    return `(${descriptionFragments.join(', ')})`;
  }

  return null;
};

export const isArchiveView = () => {
  const state = getState();
  if (state) {
    return state.Files.params.archived;
  }

  return false;
};

export const getFileAvailableFilter = (field) => {
  const state = getState();
  if (state) {
    return state.Files.availableFilters[field];
  }

  return false;
};

export const getCurrentFileFilters = () => {
  const state = getState();
  if (state) {
    return {
      ...state.Files.filters,
      search: state.Search.search,
      query_type: state.Search.query_type,
      search_fields: state.Search.search_fields,
      collectionUuid: state.Files.collectionUuid,
    };
  }

  return false;
};

export const getMinFileCreatedAt = () => (
  getFileAvailableFilter('min_created_at')
);

export const getMinFileOriginalDatetime = () => (
  getFileAvailableFilter('min_original_datetime')
);

export const getMinFileExpirationDatetime = () => (
  getFileAvailableFilter('min_expiration_datetime')
);

export const printFile = (fileUrl) => {
  if (getFileExtension({ path: fileUrl.split('?')[0] }) === 'PDF') {
    const pdfPrint = window.open(`${window.location.origin}/print/?file=${encodeURIComponent(fileUrl)}`);
    pdfPrint.addEventListener('load', () => {
      pdfPrint.print();
    }, true);
  } else {
    const imagePrint = window.open('');
    imagePrint.document.write(`
      <html>
        <head>
          <style type="text/css">
            @page { margin: 0; }
            html, body { height: 99%; overflow: hidden; text-align: center; }
            img { max-width: 100%; max-height: 100%; }
          </style>
        </head>
        <body>
          <img src="${fileUrl}" alt="" />
        </body>
      </html>
    `);
    imagePrint.document.close();
    imagePrint.addEventListener('load', () => {
      imagePrint.print();
    }, true);
  }
};

export const cleanUpExifTags = (tags) => {
  const tagsToRemove = [
    'MakerNote',
    'DocumentAncestors',
    'Thumbnail',
    'PrintIM',
    'Images',
    'ApplicationNotes',
    'PhotoshopSettings',
    'ICC_Profile',
    'ImageSourceData',
    'History',
  ];

  const cleanedTags = tags;
  tagsToRemove.forEach((tag) => {
    if (cleanedTags[tag]) {
      delete cleanedTags[tag];
    }
  });

  return cleanedTags;
};

export const removeUnwantedFiles = (files) => {
  const fnames = [
    '.ds_store',
    '.appledouble',
    '.lsoverride',
    '.localized',
    'thumbs.db',
  ];

  return files
    .filter(f => (
      !fnames.includes(getFileNameFromPath(f.name.toLowerCase())) &&
      getFileNameFromPath(f.name.toLowerCase()).indexOf('._') !== 0
    ));
};

export const isAllFilesPage = () => (
  window.location.pathname === '/files' ||
  window.location.pathname === '/archive/files' ||
  window.location.pathname === '/embed/files' ||
  window.location.pathname === '/embed/archive/files'
);

export const canBeUsedImageEditor = (file) => (
  ['jpg', 'jpeg', 'png', ...rawImageFileExtensions].includes(file.extension)
);

export const fileImageEditorNeedsConvert = (file) => (
  [...rawImageFileExtensions].includes(file.extension)
);

export const retrieveSizeAdjustedVideoAnnotations =
  (annotations, currentStageWidth, currentStageHeight) => annotations.map(a => {
    const widthRatio = currentStageWidth / a.meta.stageWidth;
    const heightRatio = currentStageHeight / a.meta.stageHeight;

    if (_.isNaN(widthRatio) || _.isNaN(heightRatio)) {
      return a;
    }

    return {
      ...a,
      meta: {
        ...a.meta,
        x: a.meta.x * widthRatio,
        y: a.meta.y * heightRatio,
        width: a.meta.width * widthRatio,
        height: a.meta.height * heightRatio,
      },
    };
  });

export const fileHasErrors = (errors) => (
  errors?.expirationDate || errors?.status
);

export const isEditionInWorkspace = (file) => (
  !((file.parent || file.parent_uuid) && file.searchable === false)
);

export const lettersToIndex = (letters) => {
  let result = 0;

  const lettersValue = letters.toLowerCase();

  for (let i = 0; i < lettersValue.length; i += 1) {
    const charCode = lettersValue.charCodeAt(i) - 96;
    result = result * 26 + charCode;
  }

  return result;
};

const indexToLetters = (inputIndex) => {
  if (inputIndex < 1) return '';

  let index = inputIndex;
  let result = '';

  while (index > 0) {
    index -= 1;
    const char = String.fromCharCode((index % 26) + 97);
    result = char + result;
    index = Math.floor(index / 26);
  }

  return result;
};

const getPrefixSuffixLabel = (index, label) => {
  if (label === 'a') return indexToLetters(index);
  if (label === 'A') return indexToLetters(index).toUpperCase();
  return index;
};

export const getFilePrefix = (index, label, separator) => `${getPrefixSuffixLabel(index, label)}${separator}`;

export const getFileSuffix = (index, label, separator) => `${separator}${getPrefixSuffixLabel(index, label)}`;

export const prefixSuffixLabels = [
  { label: 'Numbers (1, 2, 3, ...)', value: '1' },
  { label: 'Letters (a, b, ..., aa, ab, ...)', value: 'a' },
  { label: 'Capital letters (A, B, ..., AA, AB, ...)', value: 'A' },
];

export const prefixSuffixSeparators = [
  { label: '_', value: '_' },
  { label: '-', value: '-' },
  { label: ',', value: ',' },
  { label: '.', value: '.' },
];

export const getDatesFiltersFromSelectedFilters = (selectedFilters) => {
  const filters = {};

  const applyCustomDateFilter = (filterType, filter, fromKey, toKey) => {
    const dateFilter = selectedFilters.find(f => f.type === filterType);
    if (!dateFilter) return;

    filters[filter] = dateFilter.key;

    if (dateFilter.key === 'custom') {
      const [dateFrom, dateTo] = dateFilter.label.split(' - ');
      filters[fromKey] = moment(`${dateFrom} 00:00:00`).format('YYYY-MM-DD HH:mm:ss');
      filters[toKey] = moment(`${dateTo} 23:59:59`).format('YYYY-MM-DD HH:mm:ss');
    }
  };

  applyCustomDateFilter('dates', 'date', 'date_from', 'date_to');
  applyCustomDateFilter('original_dates', 'original_date', 'original_date_from', 'original_date_to');
  applyCustomDateFilter('expiration_dates', 'expiration_date', 'expiration_date_from', 'expiration_date_to');

  return filters;
};

export const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();

  reader.onload = () => resolve(reader.result);
  reader.onerror = (error) => reject(error);

  reader.readAsDataURL(file);
});

export const isFileProcessing = (file, job) => (
  ['queued', 'processing'].includes(file.attributes?.[job])
);

export const getImageEditorSaveOriginalName = (file, shouldForcePNG) => {
  const originalName = file.original_name;

  if (!shouldForcePNG) {
    return originalName;
  }

  const nameWithoutExt = getFileNameWithoutExtension(file.original_name, getFileExtension(file));
  return `${nameWithoutExt}.png`;
};

export const getDefaultMetadataGroupId = () => {
  const { metadataGroups } = getState().Metadata;
  const defaultGroup = metadataGroups.length > 0 ? metadataGroups.find(tg => tg.default) : null;
  return defaultGroup ? defaultGroup.id : null;
};

export const pdfViewerV2 = () => (
  (isShare() && getSetting('pdf_v2_share_enabled')) || (!isShare() && getSetting('pdf_v2_workspace_enabled'))
);

export const userCanEditPeople = (file) => file && (userCan('file.edit', file) || userHasCap('edit_people'));

export const getFilesView = () => (getUserSetting('files_view') || getSetting('default_files_view'));

export const getLoadedFilesLabel = (files, filesTotal) => {
  if (filesTotal === 0) {
    return null;
  }

  return `(${files.length} of ${filesTotal})`;
};
